import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { makeReference } from '@apollo/client';
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { usePageEventsTracker } from '@infl-ts/util-page-events-tracker';
import { combineMutationStatuses, useMutationMapper } from '@infl-ts/util-use-mutation-mapper';
import { Box, FormControl, Typography } from '@mui/material';
import * as E from 'fp-ts/Either';
import { constant, constVoid, flow, pipe } from 'fp-ts/function';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AsyncButton, SaveStates } from '../../../async-button';
import { TextFormField } from '../../../form';
import { ImageUpload } from '../../../image-upload';
import { modalPages, onboardingCategories } from '../../constants';
import { imageRemoveFailure, imageSubmitFailure, statusUpdateFailure, submitFailure, submitSuccess } from './alerts';
import { updateOnboardingProfile, updateOnboardingProfileImage, updateOnboardingStatus } from './mutations';
import SkipButton from './skip-button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var useProps = function useProps(_ref) {
  var onFinish = _ref.onFinish,
      profile = _ref.profile;
  var profileForm = useForm({
    defaultValues: profile
  });

  var _usePageEventsTracker = usePageEventsTracker(),
      startActivity = _usePageEventsTracker.startActivity,
      trackClick = _usePageEventsTracker.trackClick;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  useEffect(function () {
    return startActivity(onboardingCategories.standard, modalPages.profile);
  }, []);

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _updateOnboardingProf = updateOnboardingProfileImage({
    onCompleted: E.matchW(function () {
      return showAlert(profile.avatarUrl ? imageSubmitFailure(t) : imageRemoveFailure(t));
    }, constVoid)
  }),
      _updateOnboardingProf2 = _slicedToArray(_updateOnboardingProf, 2),
      updateProfileImage = _updateOnboardingProf2[0],
      profileImageMutationStatus = _updateOnboardingProf2[1];

  var _updateOnboardingProf3 = updateOnboardingProfile({
    onCompleted: E.match(function () {
      return showAlert(submitFailure(handleSaveChanges, t));
    }, function (_ref2) {
      var editProfile = _ref2.editProfile;

      if (editProfile.__typename === 'EditProfileResult') {
        trackClick('Update Profile', onboardingCategories.standard, modalPages.profile);
        showAlert(submitSuccess(t));
        onFinish();
      } else {
        showAlert(submitFailure(handleSaveChanges, t));
      }
    })
  }),
      _updateOnboardingProf4 = _slicedToArray(_updateOnboardingProf3, 2),
      editProfile = _updateOnboardingProf4[0],
      profileMutationStatus = _updateOnboardingProf4[1];

  var _updateOnboardingStat = updateOnboardingStatus({
    onCompleted: E.match(function () {
      return showAlert(statusUpdateFailure(t));
    }, function (_ref3) {
      var updateOnboardingStatus = _ref3.updateOnboardingStatus;
      if (updateOnboardingStatus.__typename === 'UpdateOnboardingStatusSuccess') trackClick('Update Onboarding Status', onboardingCategories.standard, modalPages.profile);else showAlert(statusUpdateFailure(t));
    })
  }),
      _updateOnboardingStat2 = _slicedToArray(_updateOnboardingStat, 2),
      updateStatus = _updateOnboardingStat2[0],
      mutationStatus = _updateOnboardingStat2[1];

  var completeOnboarding = function completeOnboarding(status) {
    void updateStatus({
      variables: {
        input: {
          status: status
        }
      }
    });
  };

  var handleSaveChanges = function handleSaveChanges() {
    void updateProfileImage({
      variables: {
        input: {
          url: profileForm.getValues('avatarUrl') || ''
        }
      }
    }).then(function (data) {
      return pipe(data, E.match(constVoid, function (_ref4) {
        var updateProfileImage = _ref4.updateProfileImage;
        if (updateProfileImage.__typename !== 'UpdateProfileImageOutput') return;
        var url = updateProfileImage.url;
        void editProfile({
          variables: {
            input: {
              name: profileForm.getValues('name'),
              title: profileForm.getValues('title'),
              company: profileForm.getValues('company')
            }
          },
          update: function update(cache, data) {
            return pipe(data, E.match(constVoid, function (_ref5) {
              var editProfile = _ref5.editProfile;
              if (editProfile.__typename !== 'EditProfileResult') return;
              trackClick('Complete Onboarding', onboardingCategories.standard, modalPages.profile);
              completeOnboarding('PASSED');
              var _editProfile$result = editProfile.result,
                  name = _editProfile$result.name,
                  title = _editProfile$result.title,
                  company = _editProfile$result.company;
              ['Profile', 'Member'].forEach(function (type) {
                return cache.modify({
                  id: cache.identify(makeReference("".concat(type, ":").concat(profile.id))),
                  fields: {
                    name: constant(name),
                    title: constant(title),
                    company: constant(company),
                    avatarUrl: constant(url)
                  }
                });
              });
              profileForm.reset(_objectSpread(_objectSpread({}, profileForm.getValues()), {}, {
                name: name,
                title: title,
                company: company,
                avatarUrl: url
              }));
            }));
          }
        });
      }));
    });
  };

  var Saving = SaveStates.Saving,
      Default = SaveStates.Default,
      Success = SaveStates.Success,
      Error = SaveStates.Error;
  var profileMutationValue = useMutationMapper(profileMutationStatus, function (_ref6) {
    var editProfile = _ref6.editProfile;
    return editProfile.__typename === 'EditProfileErrors' ? E.left(editProfile) : E.right(editProfile);
  }, [Saving, Success, Error, Default]);
  var profileImageMutationValue = useMutationMapper(profileImageMutationStatus, function (_ref7) {
    var updateProfileImage = _ref7.updateProfileImage;
    return updateProfileImage.__typename !== 'UpdateProfileImageOutput' ? E.left(updateProfileImage) : E.right(updateProfileImage);
  }, [Saving, Success, Error, Default]);
  return {
    profile: profile,
    profileForm: profileForm,
    handleSaveChanges: handleSaveChanges,
    onFinish: onFinish,
    profileMutationValue: profileMutationValue,
    profileImageMutationValue: profileImageMutationValue,
    trackClick: trackClick,
    completeOnboarding: completeOnboarding,
    mutationStatus: mutationStatus,
    t: t
  };
};

export var PureOnboardingProfileForm = function PureOnboardingProfileForm(_ref8) {
  var profile = _ref8.profile,
      _ref8$profileForm = _ref8.profileForm,
      control = _ref8$profileForm.control,
      watch = _ref8$profileForm.watch,
      handleSubmit = _ref8$profileForm.handleSubmit,
      _ref8$profileForm$for = _ref8$profileForm.formState,
      errors = _ref8$profileForm$for.errors,
      isValid = _ref8$profileForm$for.isValid,
      profileForm = _ref8.profileForm,
      handleSaveChanges = _ref8.handleSaveChanges,
      onFinish = _ref8.onFinish,
      profileMutationValue = _ref8.profileMutationValue,
      profileImageMutationValue = _ref8.profileImageMutationValue,
      completeOnboarding = _ref8.completeOnboarding,
      trackClick = _ref8.trackClick,
      t = _ref8.t;
  return /*#__PURE__*/_jsxs(Box, {
    "data-element": "Box",
    "data-component": "PureOnboardingProfileForm",
    "data-source-file": "index.tsx",
    children: [/*#__PURE__*/_jsx(Box, {
      sx: {
        textAlign: 'center',
        mb: 4
      },
      "data-element": "Box",
      "data-component": "PureOnboardingProfileForm",
      "data-source-file": "index.tsx",
      children: /*#__PURE__*/_jsx(Typography, {
        variant: "h2",
        "data-element": "Typography",
        "data-component": "PureOnboardingProfileForm",
        "data-source-file": "index.tsx",
        children: t('Fill out your profile')
      })
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        display: 'flex',
        justifyContent: 'center'
      },
      "data-element": "Box",
      "data-source-file": "index.tsx",
      children: pipe(watch('avatarUrl'), function (watchAvatarUrl) {
        return /*#__PURE__*/_jsx(ImageUpload, {
          member: _objectSpread(_objectSpread({}, profile), {}, {
            avatarUrl: watchAvatarUrl
          }),
          buttonOrientation: "column",
          onUpload: function onUpload(newAvatarUrl) {
            return profileForm.setValue('avatarUrl', newAvatarUrl);
          }
        });
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-source-file": "index.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "name",
        control: control,
        required: true,
        label: t('Name'),
        error: errors.name,
        "data-element": "TextFormField",
        "data-source-file": "index.tsx"
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      sx: {
        width: 1,
        mb: 2,
        mr: 3
      },
      "data-element": "FormControl",
      "data-source-file": "index.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "title",
        control: control,
        label: t('Job Title'),
        error: errors.title,
        "data-element": "TextFormField",
        "data-source-file": "index.tsx"
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-source-file": "index.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "company",
        control: control,
        label: t('Company'),
        error: errors.company,
        "data-element": "TextFormField",
        "data-source-file": "index.tsx"
      })
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      },
      "data-element": "Box",
      "data-source-file": "index.tsx",
      children: [/*#__PURE__*/_jsx(AsyncButton, {
        status: combineMutationStatuses([profileMutationValue, profileImageMutationValue]),
        disabled: !isValid,
        onClick: handleSubmit(handleSaveChanges),
        text: [t('Saving...'), t('Saved'), t('Save changes'), t('Save changes')],
        "data-element": "AsyncButton",
        "data-source-file": "index.tsx"
      }), /*#__PURE__*/_jsx(SkipButton, {
        onClick: function onClick() {
          trackClick('Skip for now', onboardingCategories.standard, modalPages.profile);
          completeOnboarding('SKIPPED');
          onFinish();
        },
        "data-element": "SkipButton",
        "data-source-file": "index.tsx"
      })]
    })]
  });
};
export default Object.assign(flow(useProps, PureOnboardingProfileForm), {
  displayName: 'OnboardingProfileForm'
});